import type { Type } from '@mend/types/appointment';
import type {
  GetActiveProviderStatsParams,
  GetAggregatedAppointmentDataParams,
  GetAggregatedFormDataParams,
  GetAppointmentSnapshotDataParams,
  GetFormCompletionTimeParams,
  GetFormsAssignedParams,
  GetFormsCompletedParams,
  GetFormsCompletionRateParams,
  GetFormSnapshotDataParams,
  GetNoShowRateParams,
  GetTotalAppointmentsParams,
  GetTotalPatientsSeenParams,
} from '#services/api/insights.ts';

import { queryOptions } from '@tanstack/react-query';

import { getAppointmentTypes } from '#services/api/appointment-type.ts';
import { getAssessments } from '#services/api/assessment.ts';
import {
  getActiveProviders,
  getActiveProviderStats,
  getAppointmentRates,
  getAppointmentsByType,
  getAppointmentSnapshotData,
  getFormCompletionByType,
  getFormCompletionTime,
  getFormCompletionTimeTrends,
  getFormsAssigned,
  getFormsCompleted,
  getFormsCompletionRate,
  getFormSnapshotData,
  getFormStatusCounts,
  getFormTrends,
  getFormTypeBreakdown,
  getNoShowRate,
  getTotalAppointments,
  getTotalPatientsSeen,
} from '#services/api/insights.ts';
import {
  getReadinessConfig,
  getRiskModelExplainer,
  listOfferableSlots,
  previewMessageContent,
  type PreviewMessageContentRequest,
} from '#services/api/mia.ts';
import {
  getPatientReadinessDetails,
  type GetPatientReadinessDetailsParams,
} from '#services/api/patient-readiness.ts';
import { getProperties } from '#services/api/properties.ts';
import { getSavedViews } from '#services/api/saved-views.ts';
import { getMe } from '#services/api/user.ts';

export const me = queryOptions({
  queryKey: ['users', 'me'],
  queryFn: getMe,
  staleTime: Infinity,
});

export const properties = queryOptions({
  queryKey: ['properties'],
  queryFn: getProperties,
  staleTime: Infinity,
});

export const savedViews = queryOptions({
  queryKey: ['savedViews'],
  queryFn: getSavedViews,
  staleTime: Infinity,
});

const patientReadinessPollingIntervalMs = 5 * 60 * 1_000; // 5 minutes

export function patientReadinessDetails(params: GetPatientReadinessDetailsParams) {
  return queryOptions({
    queryKey: ['patient-readiness-details', params],
    queryFn: () => getPatientReadinessDetails(params),
    staleTime: 0,
    refetchOnMount: true,
    // The worklist reflects live confirmation state (patients confirm out-of-band), so keep
    // polling — there is no terminal "snapshot" state that would make refetching pointless.
    refetchInterval: patientReadinessPollingIntervalMs,
  });
}

export function miaMessagePreview(request: PreviewMessageContentRequest) {
  return queryOptions({
    queryKey: ['mia-message-preview', request],
    queryFn: () => previewMessageContent(request),
    // The generated copy is stable for a given appointment/channel — don't re-generate (and
    // re-bill the LLM) every time the preview is shown again in the same session.
    staleTime: Infinity,
  });
}

export function riskModelExplainer(params: { orgId: number }) {
  return queryOptions({
    queryKey: ['risk-model-explainer', params],
    queryFn: () => getRiskModelExplainer(params),
    // The deployed model's feature importance is refreshed by the analytics layer on its own
    // (daily) cadence — nothing a session can do changes it.
    staleTime: Infinity,
  });
}

export function readinessConfig(params: { orgId: number }) {
  return queryOptions({
    queryKey: ['readiness-config', params],
    queryFn: () => getReadinessConfig(params),
    // Any of the org's admins can change this, from any session — so a saved threshold arrives
    // out-of-band exactly like a patient's confirmation does. Polled on the worklist's interval
    // rather than a longer one because the two are applied to each other: the briefing flags rows
    // at or above this threshold, and refreshing the rows while holding an hour-old floor would
    // judge new appointments by a threshold nobody is using any more.
    staleTime: 0,
    refetchOnMount: true,
    refetchInterval: patientReadinessPollingIntervalMs,
  });
}

export function offerableSlots(params: { orgId: number }) {
  return queryOptions({
    queryKey: ['offerable-slots', params],
    queryFn: () => listOfferableSlots(params),
    staleTime: 0,
    refetchOnMount: true,
    // A slot's offer state can change from a patient's reply, not just a staff click — keep
    // polling like patientReadinessDetails rather than relying solely on post-mutation refetches.
    refetchInterval: patientReadinessPollingIntervalMs,
  });
}

export function formTrends(params: GetAggregatedFormDataParams) {
  return queryOptions({
    queryKey: ['formTrends', params],
    queryFn: () => getFormTrends(params),
    staleTime: Infinity,
  });
}

export function formCompletionByType(params: GetAggregatedFormDataParams) {
  return queryOptions({
    queryKey: ['formCompletionByType', params],
    queryFn: () => getFormCompletionByType(params),
    staleTime: Infinity,
  });
}

export function appointmentRates(params: GetAggregatedAppointmentDataParams) {
  return queryOptions({
    queryKey: ['appointmentRates', params],
    queryFn: () => getAppointmentRates(params),
    staleTime: Infinity,
  });
}

export function appointmentsByType(params: GetAggregatedAppointmentDataParams) {
  return queryOptions({
    queryKey: ['appointmentsByType', params],
    queryFn: () => getAppointmentsByType(params),
    staleTime: Infinity,
  });
}

export function noShowRate(params: GetNoShowRateParams) {
  return queryOptions({
    queryKey: ['noShowRate', params],
    queryFn: () => getNoShowRate(params),
    staleTime: Infinity,
  });
}

export function appointmentSnapshot(params: GetAppointmentSnapshotDataParams) {
  return queryOptions({
    queryKey: ['appointmentSnapshot', params],
    queryFn: () => getAppointmentSnapshotData(params),
    staleTime: Infinity,
  });
}

export function totalPatientsSeen(params: GetTotalPatientsSeenParams) {
  return queryOptions({
    queryKey: ['totalPatientsSeen', params],
    queryFn: () => getTotalPatientsSeen(params),
    staleTime: Infinity,
  });
}

export function formCompletionTime(params: GetFormCompletionTimeParams) {
  return queryOptions({
    queryKey: ['formCompletionTime', params],
    queryFn: () => getFormCompletionTime(params),
    staleTime: Infinity,
  });
}

export function activeProviders(params: GetAggregatedAppointmentDataParams) {
  return queryOptions({
    queryKey: ['activeProviders', params],
    queryFn: () => getActiveProviders(params),
    staleTime: Infinity,
  });
}

export function activeProviderStats(params: GetActiveProviderStatsParams) {
  return queryOptions({
    queryKey: ['activeProviderStats', params],
    queryFn: () => getActiveProviderStats(params),
    staleTime: Infinity,
  });
}

export function formCompletionTimeTrends(params: GetAggregatedFormDataParams) {
  return queryOptions({
    queryKey: ['formCompletionTimeTrends', params],
    queryFn: () => getFormCompletionTimeTrends(params),
    staleTime: Infinity,
  });
}

export function formTypeBreakdown(params: GetAggregatedFormDataParams) {
  return queryOptions({
    queryKey: ['formTypeBreakdown', params],
    queryFn: () => getFormTypeBreakdown(params),
    staleTime: Infinity,
  });
}

export function formStatusCounts(params: GetAggregatedFormDataParams) {
  return queryOptions({
    queryKey: ['formStatusCounts', params],
    queryFn: () => getFormStatusCounts(params),
    staleTime: Infinity,
  });
}

export function formSnapshot(params: GetFormSnapshotDataParams) {
  return queryOptions({
    queryKey: ['formSnapshot', params],
    queryFn: () => getFormSnapshotData(params),
    staleTime: Infinity,
  });
}

export function appointmentTypes(type?: Type) {
  return queryOptions({
    queryKey: ['appointment-types', type],
    queryFn: () => getAppointmentTypes(type),
    staleTime: Infinity,
  });
}

export const assessments = queryOptions({
  queryKey: ['assessments'],
  queryFn: getAssessments,
  staleTime: Infinity,
});

export function formsAssigned(params: GetFormsAssignedParams) {
  return queryOptions({
    queryKey: ['formsAssigned', params],
    queryFn: () => getFormsAssigned(params),
    staleTime: Infinity,
  });
}

export function formsCompleted(params: GetFormsCompletedParams) {
  return queryOptions({
    queryKey: ['formsCompleted', params],
    queryFn: () => getFormsCompleted(params),
    staleTime: Infinity,
  });
}

export function formsCompletionRate(params: GetFormsCompletionRateParams) {
  return queryOptions({
    queryKey: ['formsCompletionRate', params],
    queryFn: () => getFormsCompletionRate(params),
    staleTime: Infinity,
  });
}

export function totalAppointments(params: GetTotalAppointmentsParams) {
  return queryOptions({
    queryKey: ['totalAppointments', params],
    queryFn: () => getTotalAppointments(params),
    staleTime: Infinity,
  });
}
